import { useEffect, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';

import { useAuth, User } from '../context/AuthContext';
import { executeBillingDataLoad, handlePaymentSuccessHelper } from '../pages/BillingPageHelpers';
import { billingService } from '../services/billingService';
import { initiateTopUp as initiateTopUpAction, initiateAppleTopUp, verifyTransaction } from '../services/paymentActions';
import { paymentService } from '../services/paymentService';
import { projectService } from '../services/projectService';
import { subscriptionService } from '../services/subscriptionService';
import { trackVerifiedPurchase } from '../utils/analytics';

import { createHandlers } from './useBillingPageHandlers';
import { useBillingPageState } from './useBillingPageState';
import { useRazorpay } from './useRazorpay';

const PAGE_SIZE = 20;
const LEDGER_PAGE_SIZE = 20;
const BILLING_TOPUP_SUCCESS_EVENT = 'billing_topup_success';

type StateType = ReturnType<typeof useBillingPageState>;
const syncCreditsAfterSuccess = async (refreshUser: () => Promise<void>) => {
  await refreshUser().catch(() => {});

  try {
    const subscription = await subscriptionService.getCurrentSubscription();

    window.dispatchEvent(new CustomEvent('credits_update', {
      detail: { credits_balance: subscription.credits_balance },
    }));
  } catch {
    // Keep the optimistic success UI even if the follow-up sync is delayed.
  }
};

function useLoadBillingData(state: StateType) {
  const {
    selectedProjectIds, currentPage, ledgerPage, appliedDateRange,
    setLoading, setError, setPaymentSuccess,
    setSummary, setAllProjects, setOutstandingBalance,
    setPaymentHistory, setProjectGroups, setPagination, setTotals, setLedgerPagination,
  } = state;

  return useCallback(
    async () => executeBillingDataLoad({
      selectedProjectIds,
      currentPage,
      pageSize: PAGE_SIZE,
      ledgerPage,
      ledgerPageSize: LEDGER_PAGE_SIZE,
      billingService,
      projectService,
      paymentService,
      setLoading,
      setError,
      setPaymentSuccess,
      dateFrom: appliedDateRange.start ? appliedDateRange.start.toISOString().split('T')[0] : null,
      dateTo: appliedDateRange.end ? appliedDateRange.end.toISOString().split('T')[0] : null,
      ledgerDateFrom: state.appliedLedgerDateRange.start ? state.appliedLedgerDateRange.start.toISOString().split('T')[0] : null,
      ledgerDateTo: state.appliedLedgerDateRange.end ? state.appliedLedgerDateRange.end.toISOString().split('T')[0] : null,
      ledgerDirection: state.appliedLedgerDirection,
      setters: {
        setSummary, setAllProjects, setOutstandingBalance, setPaymentHistory,
        setProjectGroups, setPagination, setTotals, setLedgerHistory: state.setLedgerHistory,
        setLedgerPagination,
      },
    }),
    [
      selectedProjectIds, currentPage, ledgerPage, appliedDateRange,
      setLoading, setError, setPaymentSuccess,
      setSummary, setAllProjects, setOutstandingBalance, setPaymentHistory,
      setProjectGroups, setPagination, setTotals, state.setLedgerHistory,
      setLedgerPagination, state.appliedLedgerDateRange,
      state.appliedLedgerDirection,
    ]
  );
}

const useAuthRedirect = (
  authLoading: boolean, isAuthenticated: boolean, loadBillingData: () => Promise<void>
) => {
  const navigate = useNavigate();

  useEffect(() => {
    if (!authLoading && !isAuthenticated) { navigate('/');

      return; }

    if (isAuthenticated) loadBillingData();
  }, [isAuthenticated, authLoading, navigate, loadBillingData]);
};

type UserType = User | null;

const dateKey = (date: Date | null): string => date?.toISOString().split('T')[0] || '';

const hasPendingLedgerFilters = (state: StateType): boolean => (
  state.ledgerDirection !== state.appliedLedgerDirection
  || dateKey(state.ledgerDateRange.start) !== dateKey(state.appliedLedgerDateRange.start)
  || dateKey(state.ledgerDateRange.end) !== dateKey(state.appliedLedgerDateRange.end)
);

const usePaymentSetup = (
  state: StateType, user: UserType, load: () => Promise<void>, refreshUser: () => Promise<void>,
) => useRazorpay({
  onSuccess: async (r) => {
    handlePaymentSuccessHelper(r, state.setPaymentSuccess, load);
    await syncCreditsAfterSuccess(refreshUser);
    window.dispatchEvent(new CustomEvent(BILLING_TOPUP_SUCCESS_EVENT, { detail: { credits: r.credits } }));
  },
  onFailure: (e) => state.setError(e),
  prefill: { name: user?.displayName || '', email: user?.email || '' },
});

const buildReturnValue = (
  authLoading: boolean, isAuthenticated: boolean, state: StateType,
  payment: ReturnType<typeof useRazorpay>, handlers: ReturnType<typeof createHandlers>,
  user: UserType, topUpHandler: (amount: number, currency?: string) => void, appleTopUpHandler: (productId: string) => void
) => ({
  authLoading, isAuthenticated,
  summary: state.summary, projectGroups: state.projectGroups, pagination: state.pagination,
  totals: state.totals, paymentHistory: state.paymentHistory,
  outstandingBalance: state.outstandingBalance, loading: state.loading,
  ledgerHistory: state.ledgerHistory, ledgerPagination: state.ledgerPagination,
  error: state.error, paymentSuccess: state.paymentSuccess, paymentError: payment.error,
  allProjects: state.allProjects, selectedProjectIds: state.selectedProjectIds,
  isFilterDropdownOpen: state.isFilterDropdownOpen,
  setIsFilterDropdownOpen: state.setIsFilterDropdownOpen,
  expandedProjects: state.expandedProjects, currentPage: state.currentPage, ledgerPage: state.ledgerPage,
  dateRange: state.dateRange, setDateRange: state.setDateRange,
  ledgerDateRange: state.ledgerDateRange, setLedgerDateRange: state.setLedgerDateRange,
  ledgerDirection: state.ledgerDirection,
  setLedgerDirection: state.setLedgerDirection,
  canApplyLedgerFilters: hasPendingLedgerFilters(state),
  handleApplyDateFilter: () => {
    state.setCurrentPage(1);
    state.setAppliedDateRange(state.dateRange);
  },
  handleApplyLedgerDateFilter: () => {
    state.setLedgerPage(1);
    state.setAppliedLedgerDateRange(state.ledgerDateRange);
    state.setAppliedLedgerDirection(state.ledgerDirection);
  },
  isProcessing: payment.isProcessing, handlePayNow: () => payment.initiatePayment(),
  handleTopUp: topUpHandler,
  onAppleTopUp: appleTopUpHandler,
  ...handlers,
  user,
});

const useBillingSetup = () => {
  const { user, isAuthenticated, isLoading: authLoading, getCurrentUser } = useAuth();
  const state = useBillingPageState();
  const loadBillingData = useLoadBillingData(state);

  // Handle Stripe Checkout return (session_id query param)
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const sessionId = params.get('session_id');

    if (sessionId && isAuthenticated) {
      subscriptionService.verifyStripeSession(sessionId)
        .then(async (verification) => {
          trackVerifiedPurchase(verification.purchase);
          state.setPaymentSuccess('Payment completed successfully via Stripe!');
          await syncCreditsAfterSuccess(getCurrentUser);
          window.dispatchEvent(new CustomEvent(BILLING_TOPUP_SUCCESS_EVENT));
          // Clean up the URL
          window.history.replaceState({}, '', window.location.pathname);
          loadBillingData();
        })
        .catch((err: unknown) => {
          const msg = err instanceof Error ? err.message : 'Failed to verify Stripe session';

          state.setError(msg);
          window.history.replaceState({}, '', window.location.pathname);
        });
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAuthenticated]);

  return { user, isAuthenticated, authLoading, state, loadBillingData, getCurrentUser };
};

const useBillingHandlers = (state: StateType) => useMemo(() => createHandlers({
  setError: state.setError, setSelectedProjectIds: state.setSelectedProjectIds,
  setCurrentPage: state.setCurrentPage, setLedgerPage: state.setLedgerPage,
  setExpandedProjects: state.setExpandedProjects,
  selectedProjectIds: state.selectedProjectIds, allProjects: state.allProjects,
  projectGroupIds: state.projectGroups.map((p) => p.project_id),
}), [
  state.setError, state.setSelectedProjectIds, state.setCurrentPage, state.setLedgerPage,
  state.setExpandedProjects, state.selectedProjectIds, state.allProjects, state.projectGroups,
]);

const createAppleTopUpHandler = (
  state: StateType, load: () => Promise<void>, user: UserType, refreshUser: () => Promise<void>,
) => async (productId: string) => {
  try {
    await initiateAppleTopUp(
      productId,
      async (creditsGranted: number) => {
        handlePaymentSuccessHelper(
          { paymentId: '', credits: creditsGranted },
          state.setPaymentSuccess,
          load,
        );
        await syncCreditsAfterSuccess(refreshUser);
        window.dispatchEvent(new CustomEvent(BILLING_TOPUP_SUCCESS_EVENT, { detail: { credits: creditsGranted } }));
      },
      (error: string) => {
        state.setError(error);
      },
      user?.id,
    );
  } catch (e: unknown) {
    state.setError(e instanceof Error ? e.message : 'Apple Top-up failed');
  }
};

const createTopUpHandler = (
  state: StateType, load: () => Promise<void>, user: UserType, refreshUser: () => Promise<void>,
) => async (amount: number, currency?: string) => {
  try {
    await initiateTopUpAction(
      amount,
      user?.email,
      async (response, _msg) => {
        await verifyTransaction(response);
        handlePaymentSuccessHelper(
          { paymentId: '', credits: amount },
          state.setPaymentSuccess,
          load,
        );
        await syncCreditsAfterSuccess(refreshUser);
        window.dispatchEvent(new CustomEvent(BILLING_TOPUP_SUCCESS_EVENT, { detail: { credits: amount } }));
      },
      currency || 'USD',
    );
  } catch (e: unknown) {
    state.setError(e instanceof Error ? e.message : 'Top-up failed');
  }
};

export const useBillingPage = () => {
  const { user, isAuthenticated, authLoading, state, loadBillingData, getCurrentUser } = useBillingSetup();
  const payment = usePaymentSetup(state, user, loadBillingData, getCurrentUser);
  const topUpHandler = createTopUpHandler(state, loadBillingData, user, getCurrentUser);
  const appleTopUpHandler = createAppleTopUpHandler(state, loadBillingData, user, getCurrentUser);

  useAuthRedirect(authLoading, isAuthenticated, loadBillingData);

  return buildReturnValue(authLoading, isAuthenticated, state, payment, useBillingHandlers(state), user, topUpHandler, appleTopUpHandler);
};
